import type { ChatMention } from "@zplanem/responses"

function escapeRegExp(s: string): string {
	return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/**
 * Renders a free-form chat message with its @-mentions highlighted. The stored
 * `@handle` snapshot is matched against the literal message text (longest
 * first, so one handle that prefixes another can't shadow it). The DISPLAYED
 * handle is resolved live from the mention's `userId` via `resolveHandle` (so a
 * later rename shows the current handle, like the app's system messages),
 * falling back to the snapshot for ex-members or the AI. Plain text when there
 * are no mentions.
 */
export function MentionText({
	text,
	mentions,
	resolveHandle,
}: {
	text: string
	mentions: ChatMention[]
	/** userId → current handle; null when not resolvable (use the snapshot). */
	resolveHandle?: (userId: string) => string | null
}) {
	if (mentions.length === 0) return <>{text}</>
	// Map each stored token "@<snapshotHandle>" → the token to DISPLAY (the live
	// handle when resolvable, else the snapshot). Matching still uses the
	// snapshot, since that's what's literally in the message text.
	const displayByToken = new Map<string, string>()
	for (const m of mentions) {
		const token = `@${m.handle}`
		const live =
			m.type === "user" && resolveHandle ? resolveHandle(m.userId) : null
		displayByToken.set(token, live ? `@${live}` : token)
	}
	const tokens = [...displayByToken.keys()].sort((a, b) => b.length - a.length)
	const re = new RegExp(`(${tokens.map(escapeRegExp).join("|")})`, "g")
	const parts = text.split(re)
	return (
		<>
			{parts.map((part, i) =>
				displayByToken.has(part) ? (
					<span
						// biome-ignore lint/suspicious/noArrayIndexKey: positional split parts are stable for one render.
						key={`${i}-${part}`}
						className="font-semibold underline decoration-1 underline-offset-2"
					>
						{displayByToken.get(part)}
					</span>
				) : (
					part
				),
			)}
		</>
	)
}
